/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path apihooks
 * @description Search hooks
 * @section 4.2.1.3
 */
import { QueryKey, UseQueryOptions } from "react-query";
import { useAppContext } from "./../context/AppCtxProvider";
import { mea_t } from "./../tplutils/utils";
import { useIdBySymbol } from "./../utils/model";
import useMeaQuery from "./useMeaQuery";
import useNavitems from "./useNavitems";
export type SearchResult = {
  id: string;
  name: string;
  typename: string;
  performance: string;
  rangecolor: string;
  JS_ATTR: string;
  parent_id: string;
  topelementid?: string;
  rangevalue?: string;
};
type SearchFilters = {
  period?: string;
  divisionId?: string;
};
const closenessToName = (a: string, b: string, isPart = false): number => {
  const parts = a.split("");
  let value = 0;
  for (let i = 0; i < b.length; i += 1) {
    if (!a[i] || !b[i]) break;
    if (a[i].toLowerCase() === b[i].toLowerCase()) value += 1;
  }
  if (a.toLowerCase().indexOf(b.toLowerCase()) >= 0) value += b.length;
  if (!isPart) {
    const parts = a.split(" ");
    if (parts.length > 1) {
      parts.forEach((p) => {
        value += closenessToName(p, b, true);
      });
    }
  }
  return value;
};
/**
 *
 * @param searchKeyword keyword to search
 *
 * @example
 *
 *  const {data, isLoading, isError} = useSearch('fire');
 */
const useSearch = (
  searchKeyword: string,
  options: Omit<
    UseQueryOptions<
      {
        data: SearchResult[];
      },
      unknown,
      {
        data: SearchResult[];
      },
      QueryKey
    >,
    "queryKey" | "queryFn"
  >,
  filters?: SearchFilters
) => {
  const ctx = useAppContext();
  const navi = useNavitems();
  const selectedPeriod = filters?.period || ctx.period;
  const selectedDivisionId = filters?.divisionId;
  const { data: themeMain, isLoading: isThemeLoading } = useIdBySymbol(
    ctx.modelId,
    `${ctx.modelId}_MAIN`
  );
  const themeRoot = themeMain?.data[0]?.topelementid; //  || 'SC.253393961.261881432';
  const searchResults = useMeaQuery<SearchResult[]>(
    String.raw`[${ctx.fullModelId}].subobjects(recursive=1, criteria="hasreferencescorecard=\"0\" AND Find(\"${searchKeyword}\", LOWER(measure.value(series=\"JS_ATTR\", period=\"Q1 / 2010\")))")`,
    String.raw`name, symbol, rangevalue, typename, value(period="${selectedPeriod}",as="performance"), rangecolor(period="${selectedPeriod}"), value(series="JS_ATTR", period="firstforseries"), related(relation=parentObjects, as="parent_id"), scorecard.topelementid(as="topelementid")`,
    undefined,
    {
      ...options,
      enabled: !isThemeLoading && !navi.isLoading,
    }
  );
  if (searchResults.isLoading) {
    return searchResults;
  }
  if (searchResults.data) {
    /**
     *
     * Checks if the Aim ID is under main navigations themes navi or not
     */
    const isValidAim = (value: string) => {
      const mainNavi = navi.data?.children || [];
      const [themes] =
        mainNavi.filter((item) => item.link.includes("themes")) || [];
      let res = false;
      if (themes) {
        themes.children.forEach((theme) => {
          theme.children.forEach((aim) => {
            if (aim._id === value) res = true;
          });
        });
      }
      return res;
    };
    searchResults.data.data = searchResults.data.data
      .filter((elem) => {
        if (selectedDivisionId && elem.topelementid !== selectedDivisionId) {
          return false;
        }
        if (
          elem.typename === "Scorecard" ||
          elem.typename === "Sub-KPI" ||
          elem.typename === "Benchmark"
        ) {
          return false;
        }
        if (elem.typename === "Theme") {
          return elem.parent_id === themeRoot;
        }
        return elem.JS_ATTR
          ? mea_t("Name", elem.JS_ATTR, ctx.language)
          : elem.name;
      })
      .filter((elem) => {
        if (elem.typename === "Aim" || elem.typename === "Priority") {
          return isValidAim(elem.id);
        }
        return true;
      })
      .sort((a, b) => {
        const getNameIndex = (t: string) => {
          if (t.indexOf("Government") >= 0) return 5;
          if (t.indexOf("Theme") >= 0) return 4;
          if (t.indexOf("Cluster") >= 0) return 4;
          if (t.indexOf("Sector") >= 0) return 4;
          if (t.indexOf("Aim") >= 0) return 3;
          if (t.indexOf("Priority") >= 0) return 3;
          if (t.indexOf("KPI") >= 0) return 2;
          if (t.indexOf("Project") >= 0) return 2;
          return 1;
        };
        let diff =
          closenessToName(
            b.JS_ATTR ? mea_t("Name", b.JS_ATTR, ctx.language) : b.name,
            searchKeyword
          ) *
            getNameIndex(b.typename) -
          closenessToName(
            a.JS_ATTR ? mea_t("Name", a.JS_ATTR, ctx.language) : a.name,
            searchKeyword
          ) *
            getNameIndex(a.typename);
        if (diff !== 0) {
          return diff;
        }
        diff = getNameIndex(b.typename) - getNameIndex(a.typename);
        if (diff !== 0) {
          return diff;
        }
        return b.name.length - a.name.length;
      });
  }
  return searchResults;
};
export default useSearch;
